import { isVelaStatus } from '@/utils/listings/statuses';
import { getSize } from '@/utils/iterable/getSize';
import { request } from '@/utils/api/request';

import { CHANNEL_ID, EBAY, ETSY, FAIRE } from '@/constants/channels';
import { ENDPOINT } from '@/constants/api';

export const products = {
  getById({ channel, db, getImagesMetadata, payload, shopId, signal }) {
    function fetchImages(response) {
      if (getSize(response.imagesById) > 0 && typeof getImagesMetadata === 'function') {
        return getImagesMetadata({ channel, response, signal });
      }

      return Promise.resolve(response.imagesById);
    }

    function fetchVideos(response) {
      function onVideosResponse(videos) {
        return Promise.resolve(videos.data);
      }

      switch (channel) {
        case FAIRE: {
          return onVideosResponse({ data: [] });
        }

        default: {
          if (!response.count) {
            return onVideosResponse({ data: [] });
          }

          return request.post({
            endpoint: ENDPOINT.WEB_V2,
            params: { db },
            payload: { listing_ids: response.products },
            signal,
            url: `/videos/${channel}/${shopId}/search`,
          })
            .then(onVideosResponse);
        }
      }
    }

    function onProductsResponse(response) {
      function onResponses([imagesById, videos]) {
        return {
          ...response,
          imagesById,
          videos,
        };
      }

      return Promise.all([fetchImages(response), fetchVideos(response)]).then(onResponses);
    }

    return request.post({
      endpoint: channel === EBAY || channel === FAIRE
        ? ENDPOINT.WEB_V2
        : ENDPOINT.WEB,
      params: { db },
      payload,
      signal,
      url: channel === EBAY || channel === FAIRE
        ? `/${channel}/shops/${shopId}/products`
        : `/shops/${shopId}/products`,
    })
      .then(onProductsResponse);
  },
  copyProducts({ db, payload, shopId }) {
    return request.post({
      url: `/shops/${shopId}/products/copy`,
      payload: payload,
      params: { db },
    });
  },
  getLinkedProducts({ db, linkId, productId, signal }) {
    return request.get({
      params: { db },
      signal,
      url: `/products/${productId}/mergedProducts/${linkId}`,
    });
  },
  bulkEditProducts({ channel, db, operations, params, shopId, status }) {
    const payload = channel === EBAY || channel === FAIRE
      ? { operations, saveOnly: isVelaStatus({ channel, status }) }
      : operations;

    return request.put({
      url: `/shops/${shopId}/products`,
      params: { db, editType: 'bulk', ...params },
      payload,
    });
  },
  editSingleProduct({ channel, db, operations, shopId, params, status }) {
    const payload = channel === EBAY || channel === FAIRE
      ? { operations, saveOnly: isVelaStatus({ channel, status }) }
      : operations;

    return request.put({
      url: `/shops/${shopId}/products`,
      params: { db, editType: 'single', ...params },
      payload,
    });
  },
  editLinkedProducts({
    db,
    existingProducts,
    linkId,
    newProducts,
    productsToEdit,
    shopId,
  }) {
    return request.post({
      url: `/shop/${shopId}/${linkId ? 'editMergedProducts' : 'mergeNewProducts'}`,
      params: { db },
      payload: { existingProductsToMerge: existingProducts, newProducts, productsToEdit },
    });
  },
  getInvalid({ attribute, channel, db, id, shopId, signal }) {
    switch (channel) {
      case EBAY:
      case FAIRE: {
        return request.post({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          payload: { id, attribute },
          url: `/${channel}/shops/${shopId}/listings/invalid`,
          signal,
        });
      }

      default: {
        return request.post({
          params: { db },
          payload: { id, attribute },
          url: `/shops/${shopId}/products/invalid`,
          signal,
        });
      }
    }
  },
  getInvalidCounts({ channel, db, id, shopId, signal }) {
    switch (channel) {
      case EBAY:
      case FAIRE: {
        return request.post({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          payload: { id },
          url: `/${channel}/shops/${shopId}/listings/invalid/count`,
          signal,
        });
      }

      default: {
        return request.post({
          url: `/shops/${shopId}/products/invalid/count`,
          params: { db },
          payload: { id },
          signal,
        });
      }
    }
  },
  search({ db, payload, shopId, signal }) {
    return request.put({
      url: `/shops/${shopId}/products/search`,
      params: { db },
      payload,
      signal,
    });
  },
  deleteProducts({ channel, db, products: productsToDelete, shopId }) {
    const operation = { type: 'products.delete', products: productsToDelete };

    switch (channel) {
      case ETSY: {
        operation.channelId = CHANNEL_ID[ETSY];
        operation.shopId = shopId;
        operation.value = 'delete';
        break;
      }

      default: {
        break;
      }
    }

    return request.delete({
      params: { db },
      payload: [operation],
      url: `/shops/${shopId}/products`,
    });
  },
  uploadFiles({ files }) {
    return request.post({
      payload: { files },
      url: '/digital_files/upload',
    });
  },
  searchByTitle({ channel, db, limit, offset, shopId, signal, title }) {
    return request.get({
      endpoint: ENDPOINT.WEB_V2,
      params: { db, limit, offset, title },
      signal,
      url: `/${channel}/${shopId}/search`,
    });
  },
  bulkOverride({ db, listings, shopId, signal }) {
    return request.post({
      endpoint: ENDPOINT.BULK_OVERRIDE,
      params: { db },
      payload: { listing_ids: listings },
      signal,
      url: `/shops/${shopId}/bulk-override`,
    });
  },
};
